import { createSlice } from '@reduxjs/toolkit';
import { FLOW_STATUS, type FlowStatus } from '@utils/constants';
import type { PayloadAction } from '@reduxjs/toolkit';

export type DemoEnvironment = 'internal_reporting' | 'tse_for_isv' | 'monetise' | 'internal_reporting_search_only' | '';

export interface CompanyInfo {
    website_url: string;
    opportunity_number: string;
    demo_environment: DemoEnvironment;
    search_mode?: string;
}

export interface CompanySector {
    industry: string[];
    sub_industry: string[];
    sector_positioning: string;
}

export interface Kpi {
    name: string;
    description: string;
    suggested_chart_type: string;
}

export interface Metrics {
    name: string;
    description: string;
    suggested_chart_type: string;
}

export interface KpiMetrics {
    kpi: Kpi[];
    metrics: Metrics[];
}

export interface Template {
    id: string;
    name: string;
    preview_url?: string;
    metadata?: Record<string, any>;
    version?: number;
}

interface MockupLabState {
    currentStep: number;
    totalSteps: number;
    stepsCompletedCount: number;
    flowStatus: FlowStatus;
    sessionId: string;
    companyInfo: CompanyInfo;
    companyOverview: string;
    lockRegenerate: boolean;
    companySector: CompanySector;
    brandKit: any;
    kpiMetrics: KpiMetrics;
    templates: any[];
    selectedTemplate: Template | null;
    publishId: string | null;
}

const initialState: MockupLabState = {
    currentStep: 0,
    totalSteps: 4,
    stepsCompletedCount: 0,
    flowStatus: FLOW_STATUS.NEW,
    sessionId: "",
    lockRegenerate: false,
    companyInfo: {
        website_url: "",
        opportunity_number: "",
        demo_environment: "",
        search_mode: "web_search",
    },
    companyOverview: "",
    companySector: {
        industry: [],
        sub_industry: [],
        sector_positioning: "",
    },
    brandKit: null,
    kpiMetrics: {
        kpi: [{
            name: "",
            description: "",
            suggested_chart_type: "",
        }],
        metrics: [{
            name: "",
            description: "",
            suggested_chart_type: "",
        }],
    },
    templates: [],
    selectedTemplate: null,
    publishId: null,
};

const mockupLabSlice = createSlice({
    name: 'mockupLab',
    initialState,
    reducers: {
        setCurrentStep(state, action: PayloadAction<number>) {
            state.currentStep = action.payload;
        },
        nextStep(state) {
            state.currentStep += 1;
        },
        prevStep(state) {
            state.currentStep -= 1;
        },
        resetStep(state) {
            state.currentStep = 0;
        },
        setStepsCompletedCount(state, action: PayloadAction<number>) {
            state.stepsCompletedCount = action.payload;
            state.flowStatus = action.payload === 0 ? FLOW_STATUS.NEW : FLOW_STATUS.IN_PROGRESS;
        },
        setFlowStatus(state, action: PayloadAction<FlowStatus>) {
            state.flowStatus = action.payload;
        },
        setCompanyInfo(state, action: PayloadAction<CompanyInfo>) {
            state.companyInfo = action.payload;
        },
        setSessionId(state, action: PayloadAction<string>) {
            state.sessionId = action.payload;
        },
        setLockRegenerate(state, action: PayloadAction<boolean>) {
            state.lockRegenerate = action.payload;
        },
        setCompanyOverview(state, action: PayloadAction<string>) {
            state.companyOverview = action.payload;
        },
        setcompanySector(state, action: PayloadAction<CompanySector>) {
            state.companySector = action.payload;
        },
        initializeProjectData: (state, action) => {
            const { id, website_url, overview, industry, sub_industry, config, template, salesforce_opp_number, flow_type } = action.payload;

            state.sessionId = id;
            state.flowStatus = FLOW_STATUS.IN_PROGRESS;

            state.companyInfo = {
                website_url,
                opportunity_number: salesforce_opp_number,
                demo_environment: flow_type,
                search_mode: "web_search",
            };

            state.companyOverview = overview;
            state.companySector = {
                industry: industry,
                sub_industry: sub_industry,
                sector_positioning: "",
            };
            state.brandKit = config?.branding;
            state.kpiMetrics = {
                kpi: config?.kpis || [],
                metrics: config?.metrics || [],
            };
            state.selectedTemplate = template;

            const hasOverview = overview && overview.trim() !== "";
            const hasIndustry = Array.isArray(industry) && industry.length > 0;
            const hasKpi = Array.isArray(state.kpiMetrics.kpi) && state.kpiMetrics.kpi.length > 0;

            if (hasOverview && hasIndustry && hasKpi && template) {
                state.stepsCompletedCount = 4;
                state.currentStep = 3;
            }
            else if (hasOverview && hasIndustry && hasKpi) {
                state.stepsCompletedCount = 3;
                state.currentStep = 2;
            } else if (hasOverview && hasIndustry) {
                state.stepsCompletedCount = 2;
                state.currentStep = 1;
            } else if (hasOverview) {
                state.stepsCompletedCount = 1;
                state.currentStep = 0;
            } else {
                state.stepsCompletedCount = 0;
                state.currentStep = 0;
            }
        },
        setBrandKit(state, action: PayloadAction<any>) {
            state.brandKit = action.payload;
        },
        setKpiMetrics(state, action: PayloadAction<KpiMetrics>) {
            state.kpiMetrics = action.payload;
        },
        setTemplates(state, action: PayloadAction<any[]>) {
            state.templates = action.payload;
        },
        setSelectedTemplate(state, action: PayloadAction<Template | null>) {
            state.selectedTemplate = action.payload;
        },
        setPublishId(state, action: PayloadAction<string | null>) {
            state.publishId = action.payload;
        },
        resetAll() {
            return initialState;
        },
    },
});

export const {
    setCurrentStep,
    nextStep,
    prevStep,
    resetStep,
    setStepsCompletedCount,
    setFlowStatus,
    setCompanyInfo,
    setSessionId,
    setLockRegenerate,
    setCompanyOverview,
    initializeProjectData,
    setcompanySector,
    setBrandKit,
    setKpiMetrics,
    setTemplates,
    setSelectedTemplate,
    setPublishId,
    resetAll
} = mockupLabSlice.actions;

export default mockupLabSlice.reducer;